import { Box, Button, CircularProgress, Grid, Tooltip, Typography } from "@mui/material"
import { useEffect, useMemo, useState } from "react"
import { TimeSeriesMultipleNodesContainer } from "@features/results/Result/TimeSeriesMultipleNodesContainer"
import { NodeResults, RunDto, RunResults } from "@custom-types/modelRun"
import NodesTable, { NodesTableRow } from "@features/results/Result/NodesTable"
import { GraphConverter } from "@store/services/converter/graphConverter"
import { GraphNodeData, GraphNodeEdgeData } from "@store/services/converter/types"
import NodeSelect from "@components/NodeSelect"
import { GraphDataViewModel, GroupNodeLabel } from "@features/graph/graphViewModel"
import MultiNodeBarChartContainer from "@features/results/Result/MultiNodeBarChartContainer"
import ThresholdChartContainer from "@features/results/Result/ThresholdChartContainer"
import {
  useGetRunsNodesResultsHistoricAggregateQuery,
  useGetRunsNodesResultsHistoricQuery,
} from "@store/services/runService"
import {
  graphSampleNodeToViewGroupNodeLabel,
  graphViewGroupNodeLabelToSampleNode,
} from "@features/graph/utils/graphUtils"
import { DataType, checkDataType } from "@features/results/Result/const"
import { BarStringsChart } from "@features/results/Result/ChartsContainer/BarStringsChart"
import { graphUrlAddNode, graphUrlRemoveNode } from "@features/graph/utils/graphUrl"
import { useSetNodeSelection } from "@features/graph/state/graphAtoms"
import { calculateLagShift } from "@utils/run"
import { convertHistoricToRunResults } from "@features/results/Result/runResult.utils"

type DrawerMultipleNodesProps = {
  graphData: GraphNodeEdgeData
  selectedNodesData: GraphNodeData[]
  onClose: () => void
  run: RunDto
}

export const DrawerMultipleNodes = ({
  graphData,
  selectedNodesData,
  onClose,
  run,
}: DrawerMultipleNodesProps) => {
  const [nodesData, setNodesData] = useState<GraphNodeData[]>(selectedNodesData)
  const [dataType, setDataType] = useState<DataType>("undefined")
  const [showBarChartAsTable, setShowBarChartAsTable] = useState(false)
  const { addNode, removeNode } = useSetNodeSelection()

  const nodeLabels = nodesData.map((data) => data.display.label)

  const { isFetching: isFetchingResults, data: historicResponse } =
    useGetRunsNodesResultsHistoricQuery({
      runKeys: [run.key],
      nodes: [...new Set(nodeLabels.map(graphViewGroupNodeLabelToSampleNode))],
    })
  const { isFetching: isFetchingAggregates, data: aggregateData } =
    useGetRunsNodesResultsHistoricAggregateQuery({
      runKeys: [run.key],
      nodes: [...new Set(nodeLabels.map(graphViewGroupNodeLabelToSampleNode))],
    })
  const isFetching = isFetchingResults || isFetchingAggregates

  const nodesResultsResponse: RunResults | undefined = useMemo(
    () => convertHistoricToRunResults(historicResponse?.results),
    [historicResponse?.results],
  )

  const lagShift = useMemo(() => calculateLagShift(historicResponse?.results), [historicResponse])

  // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  const nodesResults: NodeResults = nodesResultsResponse?.[run.key] ?? ({} as NodeResults)

  useEffect(() => {
    const nodeRunResults = Object.values(nodesResults)
    if (nodeRunResults.length === 0) {
      return
    }
    let dataType: DataType = checkDataType(nodeRunResults[0])
    nodeRunResults.forEach((node) => {
      if (dataType !== checkDataType(node)) {
        dataType = "undefined"
      }
    })

    setDataType(dataType)
  }, [nodesResults])

  const handleNodesTableRowDelete = (row: NodesTableRow) => {
    graphUrlRemoveNode(row.id)
    removeNode(row.id)
    setNodesData((prev) => prev.filter((data) => data.node.name !== row.id))
  }

  const handleChangeNodeSelect = (labelOption: GroupNodeLabel | null) => {
    if (!labelOption) {
      return
    }
    // ms: prevent duplicates
    if (nodesData.find((data) => data.node.name === labelOption.key)) return

    const nodeData = GraphDataViewModel.data!.nodeMap[labelOption.key]
    if (!nodeData) return
    graphUrlAddNode(nodeData.display.label)
    const node = GraphConverter.createNodeFromNodeData(nodeData)
    addNode(node)
    setNodesData([...nodesData, nodeData])
  }

  return (
    <Grid container direction="column">
      <Grid container item sx={{ width: "100%" }}>
        <Grid sx={{ p: 2 }} container>
          <Grid item>
            <Typography sx={{ fontWeight: 700 }} variant={"h5"}>
              Comparison Workspace
            </Typography>
            <Typography variant={"h6"}>
              Choose which nodes to bring into this workspace, then configure the chart(s)
              appropriately
            </Typography>
          </Grid>
          <Grid item xs>
            <Grid container direction="row-reverse">
              <Grid item>
                <Button onClick={onClose} variant="contained">
                  Close
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Grid sx={{ pr: "20px", pl: "20px", pb: "20px", width: "100%" }}>
        <Grid item sx={{ pb: "20px" }}>
          <Tooltip title={nodesData.length >= 10 ? "You can not add more than 10 nodes" : ""}>
            <NodeSelect
              graph={graphData}
              value={null}
              onChange={handleChangeNodeSelect}
              getOptionDisabled={nodeLabels.map((n) => ({ label: n, reason: "" }))}
              disabled={nodesData.length >= 10}
              label="Add node results"
              clearOnBlur
              sx={{
                backgroundColor: "white",
                p: 1,
                borderRadius: "4px",
                width: "calc(100%-20px)",
              }}
            />
          </Tooltip>
        </Grid>
        <Grid item>
          <NodesTable nodesData={nodesData} onRowDelete={handleNodesTableRowDelete} />
        </Grid>
      </Grid>
      {dataType === "undefined" && isFetching && (
        <Box sx={{ display: "flex", justifyContent: "center", my: 6 }}>
          <CircularProgress />
        </Box>
      )}
      {dataType === "undefined" && !isFetching && (
        <Grid item sx={{ pb: "20px" }}>
          <Typography sx={{ fontWeight: 700, p: 2 }} variant="h5">
            The selected nodes have different data types.
          </Typography>
        </Grid>
      )}
      {dataType === "string" && (
        <Grid container item gap={1} sx={{ p: 2, width: "100%" }}>
          <BarStringsChart
            runs={[]}
            nodes={nodeLabels.map((x) => graphSampleNodeToViewGroupNodeLabel(x))}
            aggregateData={aggregateData}
            lagShift={lagShift}
            isLoading={isFetching}
          />
        </Grid>
      )}
      {dataType === "number" && (
        <>
          <Grid container item direction="column" gap={1} sx={{ p: 2 }}>
            <Grid item sx={{ width: "100%" }}>
              <Typography sx={{ fontWeight: 700 }} variant="h5">
                Time series chart
              </Typography>
            </Grid>
            <Grid item sx={{ width: "100%" }}>
              <TimeSeriesMultipleNodesContainer
                selectedNodes={nodeLabels}
                isLoading={isFetching}
                run={run}
                historicNodes={historicResponse?.results}
              />
            </Grid>
          </Grid>
          <Grid item container direction="column" gap={1} sx={{ p: 2, pt: 4 }}>
            <Grid item sx={{ width: "100%" }}>
              <Typography sx={{ fontWeight: 700 }} variant="h5">
                Stacked bar chart
              </Typography>
            </Grid>
            <Grid item sx={{ width: "100%" }}>
              <MultiNodeBarChartContainer
                lag={lagShift}
                title={"Multinode Bar - " + run.key}
                graphData={graphData}
                nodesData={nodesData}
                runResults={nodesResultsResponse}
                isLoading={isFetching}
                runs={[run]}
                containerSx={{ width: "100%" }}
                // keep the whole chart block within one screen
                chartHeight="calc(90vh - 130px)"
                maintainAspectRatio={false}
                showAsTable={showBarChartAsTable}
                onShowAsTable={() => setShowBarChartAsTable((value) => !value)}
              />
            </Grid>
          </Grid>
          <Grid item container direction="column" gap={1} sx={{ p: 2 }}>
            <Grid item sx={{ width: "100%" }}>
              <Typography sx={{ fontWeight: 700 }} variant="h5">
                Threshold chart
              </Typography>
            </Grid>
            <Grid item sx={{ width: "100%" }}>
              <ThresholdChartContainer
                graphData={graphData}
                lag={lagShift}
                nodesData={nodesData}
                nodesResults={nodesResults}
                isLoading={isFetching}
                run={run}
                localStoragePrefix="compare-nodes"
              />
            </Grid>
          </Grid>
        </>
      )}
    </Grid>
  )
}
